import classNames from "classnames/bind";
import React, { ReactNode, useState, type PropsWithChildren } from "react";
import { Button } from "../../Button";
import { Card } from "../../Card/Card";
import { DropdownMenu } from "../../DropdownMenu";
import { IconLoading } from "../../Icon";
import { Skeleton } from "../../Skeleton";
import { Tooltip } from "../../Tooltip";
import { colours } from "../../constants/colours";
import { extractDeepestChildText } from "../../utils/dom";
import { useIsOverflow } from "../../utils/hooks/useIsOverflow";
import styles from "./ListCard.module.scss";
import { ListCardProps, type FieldSegmentProps } from "./types";
const cx = classNames.bind(styles);

const FieldSegment = ({ children, className }: FieldSegmentProps) => {
  return <div className={className}>{children}</div>;
};

const TitleFieldSegment = (
  props: PropsWithChildren<{
    className?: string;
  }>
) => (
  <FieldSegment
    className={cx("title-segment", "clamp", props.className)}
    {...props}
  />
);

const DescriptionFieldSegment = (
  props: PropsWithChildren<{
    className?: string;
  }>
) => (
  <FieldSegment
    className={cx("description-segment", "clamp", props.className)}
    {...props}
  />
);

const MetaFieldSegment = (
  props: PropsWithChildren<{
    className?: string;
  }>
) => (
  <FieldSegment
    className={cx("meta-segment", props.className)}
    {...props}
  />
);

/**
 * A Card with actions that serves as a vertically-arranged listing mechanism for items with a few fields.
 */
const ListCard = ({
  renderTitle,
  renderDescription,
  renderMeta,
  actions,
  action,
  isSelected = false,
  isDisabled = false,
  isLoading = false,
  isBusy = false,
  hasBorderBottom = true,
  colorVariant = "default",
  className,
  style,
  onClick,
  as: asProp,
  ...restProps
}: ListCardProps) => {
  const Tag = asProp ?? "div";

  const titleFieldSegment: ReactNode = renderTitle({ FieldSegment });
  const descriptionFieldSegment: ReactNode = renderDescription?.({
    FieldSegment,
  });
  const title = extractDeepestChildText(titleFieldSegment);
  const description = extractDeepestChildText(descriptionFieldSegment);

  const [isOverflowing, handleOverflowRef] = useIsOverflow();
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  return (
    <Tag
      className={cx("container")}
      tabIndex={0}
      {...restProps}
    >
      <Card
        className={cx(
          "list-card",
          colorVariant && `list-card--${colorVariant}`,
          isDisabled && "is-disabled",
          isLoading && "is-loading",
          isBusy && "is-busy",
          isSelected && "is-selected",
          hasBorderBottom && "has-border-bottom",
          onClick && "has-on-click",
          className
        )}
        style={style}
        onClick={onClick}
      >
        <div
          className={cx("main", actions && actions.length > 0 && "has-actions")}
        >
          <div>
            <Skeleton
              isLoading={isLoading}
              isBusy={isBusy}
            >
              <div
                ref={handleOverflowRef}
                className={cx("field-container")}
              >
                {isOverflowing ? (
                  <Tooltip
                    title={title}
                    content={description}
                    contentClassName={cx("description-tooltip")}
                    position={["bottom", "top", "left", "right"]}
                  >
                    {renderTitle({ FieldSegment: TitleFieldSegment })}
                  </Tooltip>
                ) : (
                  <>{renderTitle({ FieldSegment: TitleFieldSegment })}</>
                )}
              </div>
            </Skeleton>
            {renderDescription ? (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
              >
                <div
                  ref={handleOverflowRef}
                  className={cx("field-container", "description-field")}
                >
                  {renderDescription({ FieldSegment: DescriptionFieldSegment })}
                </div>
              </Skeleton>
            ) : null}
            {renderMeta ? (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
              >
                <div
                  ref={handleOverflowRef}
                  className={cx("field-container", "meta-field")}
                >
                  {renderMeta({ FieldSegment: MetaFieldSegment })}
                </div>
              </Skeleton>
            ) : null}
          </div>

          <div className={cx("actions-container")}>
            <Skeleton
              isLoading={isLoading}
              isBusy={isBusy}
            >
              <div
                className={cx(
                  "actions",
                  (isBusy || isActionsMenuOpen) && "always-visible",
                  isBusy && "is-busy-icon-container"
                )}
              >
                {isBusy ? (
                  <IconLoading
                    speed="fast"
                    colour={colours.surface["300"]}
                    size={24}
                  />
                ) : actions ? (
                  <DropdownMenu
                    options={actions}
                    rootProps={{ onOpenChange: setIsActionsMenuOpen }}
                  >
                    <Button
                      variant="outline"
                      icon="dots-three-vertical"
                      size="xx-small"
                    />
                  </DropdownMenu>
                ) : action ? (
                  <Button
                    {...action}
                    size="xx-small"
                  />
                ) : null}
              </div>
            </Skeleton>
          </div>
        </div>
      </Card>
    </Tag>
  );
};

export default ListCard;
